import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Sparkles, Loader2, Send, FileDown, CheckCircle2, TriangleAlert, MessageSquare, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVEL = ["Unseen", "Exposed", "Familiar", "Understands", "Can apply", "Can teach"];

export default function AdvisorShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [author, setAuthor] = useState(localStorage.getItem("aria_advisor_name") || "");
  const [email, setEmail] = useState(localStorage.getItem("aria_advisor_email_addr") || "");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await axios.get(`${API}/advisor/share/${token}`); setData(data); }
    catch (e) { setErr(e?.response?.data?.detail || "This link is not valid."); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const send = async () => {
    if (text.trim().length < 3) return;
    setBusy(true);
    try {
      await axios.post(`${API}/advisor/share/${token}/comments`, { author, text, email });
      localStorage.setItem("aria_advisor_name", author);
      localStorage.setItem("aria_advisor_email_addr", email);
      setText("");
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-[#030711] text-white flex items-center justify-center p-8"><div className="aria-card p-8 max-w-md text-center" data-testid="advisor-error">{err}</div></div>;
  if (!data) return <div className="min-h-screen bg-[#030711] flex items-center justify-center"><Loader2 className="animate-spin text-[var(--aria-primary)]" /></div>;
  const s = data.stats || {};
  const first = data.student.split(" ").pop();

  return (
    <div className="min-h-screen bg-[#030711] text-white" data-testid="advisor-share-page">
      <div className="max-w-3xl mx-auto p-6 sm:p-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--aria-primary-glow)] font-semibold flex items-center gap-2 mb-2"><Sparkles size={12} /> ARIA · Interview debrief shared by {data.student}</div>
        <h1 className="font-display text-3xl mb-1" data-testid="advisor-target">{data.target}</h1>
        <div className="text-sm text-slate-400 mb-6">{data.date ? new Date(data.date).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" }) : ""} · {data.mode === "voice" ? "Voice" : "Text"} mock interview
          <a href={`${process.env.REACT_APP_BACKEND_URL}${data.pdf_url}`} target="_blank" rel="noreferrer" data-testid="advisor-pdf-link" className="ml-3 text-[var(--aria-primary-glow)] hover:underline inline-flex items-center gap-1"><FileDown size={12} /> Full PDF</a>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[["Answered", s.questions_answered], ["Concepts", s.concepts_covered], ["Gaps closed", `${s.gaps_closed ?? 0}/${s.gaps_found ?? 0}`], ["Mastery gained", `+${s.levels_gained ?? 0}`]].map(([l, v]) => (
            <div key={l} className="rounded-md bg-white/5 border border-white/10 px-3 py-2.5"><div className="font-display text-2xl">{v ?? 0}</div><div className="text-[11px] uppercase tracking-wider text-slate-400">{l}</div></div>
          ))}
        </div>

        <p className="text-[15px] leading-relaxed text-slate-100 mb-6" data-testid="advisor-summary">{data.summary}</p>

        {data.mastery_changes?.length > 0 && (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-2">Mastery changes (evidence scale 0–5)</div>
            <div className="space-y-1.5">
              {data.mastery_changes.map(c => (
                <div key={c.concept} className="flex items-center justify-between text-sm px-3 py-2 rounded-md bg-white/5"><span>{c.concept}</span>
                  <span className="font-mono text-xs text-slate-300">{LEVEL[c.before]} → <span className={c.delta > 0 ? "text-emerald-400" : ""}>{LEVEL[c.after]}</span></span></div>
              ))}
            </div>
          </div>
        )}

        {data.gaps?.length > 0 && (
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-2">Gaps ARIA found</div>
            {data.gaps.map((g, i) => (
              <div key={`${g.concept}-${i}`} className="flex items-start gap-2 text-sm mb-1.5">{g.resolved ? <CheckCircle2 size={14} className="text-emerald-400 mt-0.5" /> : <TriangleAlert size={14} className="text-amber-400 mt-0.5" />}<div><b>{g.concept}</b> — {g.resolved ? "closed during the interview" : "still open"}{g.detail ? <div className="text-xs text-slate-400">{g.detail}</div> : null}</div></div>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {data.strengths?.length > 0 && <div><div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Strengths</div><ul className="text-sm text-slate-300 list-disc pl-4 space-y-1">{data.strengths.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
          {data.focus_next?.length > 0 && <div><div className="text-xs uppercase tracking-[0.2em] text-amber-400 font-semibold mb-2">Focus next</div><ul className="text-sm text-slate-300 list-disc pl-4 space-y-1">{data.focus_next.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
        </div>

        <div className="aria-card p-6 ring-glow-primary" data-testid="advisor-comments">
          <div className="flex items-center gap-2 mb-1"><MessageSquare size={15} className="text-[var(--aria-primary-glow)]" /><span className="text-xs uppercase tracking-[0.2em] font-semibold">Your comments for {first}</span></div>
          <p className="text-xs text-slate-400 mb-4">Anything you write here appears inside {first}'s ARIA next to this debrief, and {first} is notified by email.</p>
          <div className="space-y-2 mb-4">
            {data.comments.map(c => (
              <div key={c.comment_id} className="rounded-md bg-white/5 border border-white/10 p-3 text-sm" data-testid={`advisor-comment-${c.comment_id}`}>
                <div className="flex items-center justify-between text-xs text-slate-400 mb-1"><span className="inline-flex items-center gap-1.5">{c.source === "email" ? <Mail size={11} /> : <MessageSquare size={11} />} {c.author}</span><span>{new Date(c.created_at).toLocaleString()}</span></div>
                <div className="whitespace-pre-line">{c.text}</div>
              </div>
            ))}
            {data.comments.length === 0 && <div className="text-sm text-slate-500">No comments yet.</div>}
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mb-2">
            <input value={author} onChange={e => setAuthor(e.target.value)} placeholder="Your name" data-testid="advisor-name-input"
              className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/15 outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder={`Your email (so ${first} can reply)`} data-testid="advisor-email-input"
              className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/15 outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
          </div>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={4} placeholder={`Feedback for ${first}…`} data-testid="advisor-comment-input"
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/15 outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm resize-none mb-3" />
          <button onClick={send} disabled={busy || text.trim().length < 3} data-testid="advisor-comment-send" className="btn btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send comment
          </button>
        </div>
        <div className="text-[11px] text-slate-500 mt-6">Private link · generated by ARIA, {data.student}'s academic intelligence system.</div>
      </div>
    </div>
  );
}
